import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, Upload, Image, Music, Save, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { toast } from 'sonner';

/**
 * Edit Track Modal - Allows editing track metadata and cover art
 */
export function EditTrackModal({ track, isOpen, onClose, onUpdate }) {
  const { token } = useAuth();
  const [title, setTitle] = useState('');
  const [genre, setGenre] = useState('');
  const [lyrics, setLyrics] = useState('');
  const [coverArtUrl, setCoverArtUrl] = useState('');
  const [coverPreview, setCoverPreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (track) {
      setTitle(track.title || '');
      setGenre(track.genre || track.primary_genre || '');
      setLyrics(track.lyrics || '');
      setCoverArtUrl(track.cover_art_url || track.cover_url || '');
      setCoverPreview(track.cover_art_url || track.cover_url || '');
    }
  }, [track]);

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, or WebP image');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be under 10MB');
      return;
    }

    try {
      setUploading(true);
      
      // Show preview immediately
      const previewUrl = URL.createObjectURL(file);
      setCoverPreview(previewUrl);

      // Upload to server
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API}/tracks/${track.track_id}/cover`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        setCoverArtUrl(data.cover_art_url);
        toast.success('Cover art uploaded!');
      } else {
        const error = await res.json();
        toast.error(error.detail?.message || error.detail || 'Upload failed');
        setCoverPreview(track.cover_art_url || track.cover_url || '');
      }
    } catch (err) {
      toast.error('Failed to upload cover art');
      setCoverPreview(track.cover_art_url || track.cover_url || '');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const updates = {};
      if (title !== track.title) updates.title = title;
      if (genre !== (track.genre || track.primary_genre)) updates.genre = genre;
      if (lyrics !== (track.lyrics || '')) updates.lyrics = lyrics;
      if (coverArtUrl !== (track.cover_art_url || track.cover_url)) {
        updates.cover_art_url = coverArtUrl;
      }

      if (Object.keys(updates).length === 0) {
        toast.info('No changes to save');
        onClose();
        return;
      }

      const res = await fetch(`${API}/tracks/${track.track_id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });

      if (res.ok) {
        const data = await res.json();
        toast.success('Track updated successfully!');
        onUpdate?.(data.track);
        onClose();
      } else {
        const error = await res.json();
        toast.error(error.detail?.message || error.detail || 'Failed to save changes');
      }
    } catch (err) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (!track) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="edit-track-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Music className="w-5 h-5" />
            Edit Track
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Cover Art Section */}
          <div className="space-y-2">
            <Label>Cover Art</Label>
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 rounded-lg overflow-hidden bg-gray-800 flex items-center justify-center">
                {coverPreview ? (
                  <img 
                    src={coverPreview.startsWith('http') || coverPreview.startsWith('blob:') 
                      ? coverPreview 
                      : `${API.replace('/api', '')}${coverPreview}`
                    }
                    alt="Cover"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Crect fill="%23374151" width="100" height="100"/%3E%3Ctext x="50" y="55" fill="%239CA3AF" font-size="10" text-anchor="middle"%3ENo Cover%3C/text%3E%3C/svg%3E';
                    }}
                  />
                ) : (
                  <Image className="w-8 h-8 text-gray-500" />
                )}
              </div>
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleCoverUpload}
                  className="hidden"
                  id="cover-upload"
                  disabled={uploading}
                />
                <label htmlFor="cover-upload">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="cursor-pointer"
                    disabled={uploading}
                    asChild
                  >
                    <span>
                      {uploading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      {uploading ? 'Uploading...' : 'Upload Cover'}
                    </span>
                  </Button>
                </label>
                <p className="text-xs text-muted-foreground mt-1">
                  JPG, PNG, or WebP. Max 10MB.
                </p>
              </div>
            </div>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Track title"
              data-testid="edit-track-title"
            />
          </div>

          {/* Genre */}
          <div className="space-y-2">
            <Label htmlFor="genre">Genre</Label>
            <Input
              id="genre"
              value={genre}
              onChange={(e) => setGenre(e.target.value)}
              placeholder="Genre"
              data-testid="edit-track-genre"
            />
          </div>

          {/* Lyrics */}
          <div className="space-y-2">
            <Label htmlFor="lyrics">Lyrics <span className="text-xs text-muted-foreground/60">(optional)</span></Label>
            <textarea
              id="lyrics"
              value={lyrics}
              onChange={(e) => setLyrics(e.target.value)}
              placeholder={`Paste full lyrics here.

Tip — use labels like [Verse 1], [Hook], [Bridge], [Outro] on their own line.
Blank lines become natural spacing.`}
              rows={10}
              className="w-full rounded-md border border-input bg-muted/20 px-3 py-2 text-sm font-mono leading-6 placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-violet-500/40 resize-y"
              data-testid="edit-track-lyrics"
            />
            <p className="text-[10px] text-muted-foreground/60">
              Fans can toggle Show/Hide Lyrics on the song page. Formatting and line breaks are preserved.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || uploading}>
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default EditTrackModal;
